import { useReducer } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { UploadStep } from "./import/UploadStep";
import { MappingStep } from "./import/MappingStep";
import { AddressStep } from "./import/AddressStep";
import { PreviewStep } from "./import/PreviewStep";
import { ResultsStep } from "./import/ResultsStep";
import type {
  ParsedFileResult,
  ColumnMapping,
  SportTypeMapping,
  ValidationIssue,
  ImportResult,
  AddressConfig,
  WizardStep,
} from "@/server/import/types";

// ---------------------------------------------------------------------------
// State & Reducer
// ---------------------------------------------------------------------------

interface WizardState {
  step: WizardStep;
  sourceType: "city" | "external_import";
  fileData: string | null;
  fileName: string | null;
  fileType: "csv" | "xlsx" | "shp" | null;
  parseResult: ParsedFileResult | null;
  columnMappings: ColumnMapping[];
  sportTypeMappings: SportTypeMapping[];
  userSportOverrides: Record<string, string>;
  validationIssues: ValidationIssue[];
  importResult: ImportResult | null;
  isLoading: boolean;
  error: string | null;
  needsGeocoding: boolean;
  addressConfig: AddressConfig | null;
}

type WizardAction =
  | { type: "SET_LOADING"; isLoading: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "SET_SOURCE_TYPE"; sourceType: "city" | "external_import" }
  | {
      type: "FILE_PARSED";
      fileData: string;
      fileName: string;
      parseResult: ParsedFileResult;
    }
  | { type: "UPDATE_MAPPINGS"; mappings: ColumnMapping[] }
  | { type: "UPDATE_SPORT_OVERRIDE"; original: string; mapped: string }
  | { type: "GOTO_STEP"; step: WizardStep }
  | { type: "SET_ADDRESS_CONFIG"; addressConfig: AddressConfig }
  | { type: "IMPORT_COMPLETE"; result: ImportResult }
  | { type: "RESET" };

const initialState: WizardState = {
  step: "upload",
  sourceType: "external_import",
  fileData: null,
  fileName: null,
  fileType: null,
  parseResult: null,
  columnMappings: [],
  sportTypeMappings: [],
  userSportOverrides: {},
  validationIssues: [],
  importResult: null,
  isLoading: false,
  error: null,
  needsGeocoding: false,
  addressConfig: null,
};

function hasCoordsMapped(mappings: ColumnMapping[]): boolean {
  return (
    mappings.some((m) => m.targetColumn === "COMBINED_COORDS") ||
    (mappings.some((m) => m.targetColumn === "lat") &&
      mappings.some((m) => m.targetColumn === "lng"))
  );
}

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.isLoading, error: null };
    case "SET_ERROR":
      return { ...state, error: action.error, isLoading: false };
    case "SET_SOURCE_TYPE":
      return { ...state, sourceType: action.sourceType };
    case "FILE_PARSED": {
      const mappings = action.parseResult.columnMappings;
      const needsGeocoding = !hasCoordsMapped(mappings);
      return {
        ...state,
        step: "mapping",
        fileData: action.fileData,
        fileName: action.fileName,
        fileType: action.parseResult.fileType,
        parseResult: action.parseResult,
        columnMappings: mappings,
        sportTypeMappings: action.parseResult.sportTypeMappings,
        validationIssues: action.parseResult.validationIssues,
        userSportOverrides: {},
        isLoading: false,
        error: null,
        needsGeocoding,
        addressConfig: null,
      };
    }
    case "UPDATE_MAPPINGS": {
      const needsGeocoding = !hasCoordsMapped(action.mappings);
      return {
        ...state,
        columnMappings: action.mappings,
        needsGeocoding,
        // Clear stale preliminary validation — real validation happens server-side
        validationIssues: [],
      };
    }
    case "UPDATE_SPORT_OVERRIDE":
      return {
        ...state,
        userSportOverrides: {
          ...state.userSportOverrides,
          [action.original]: action.mapped,
        },
      };
    case "GOTO_STEP":
      return { ...state, step: action.step, error: null };
    case "SET_ADDRESS_CONFIG":
      return { ...state, addressConfig: action.addressConfig };
    case "IMPORT_COMPLETE":
      return {
        ...state,
        step: "results",
        importResult: action.result,
        isLoading: false,
      };
    case "RESET":
      return { ...initialState };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

const ALL_STEPS: { key: WizardStep; label: string }[] = [
  { key: "upload", label: "Upload" },
  { key: "mapping", label: "Map Columns" },
  { key: "address", label: "Address" },
  { key: "preview", label: "Preview" },
  { key: "results", label: "Results" },
];

function getVisibleSteps(needsGeocoding: boolean) {
  return needsGeocoding
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s.key !== "address");
}

function Stepper({
  currentStep,
  needsGeocoding,
}: {
  currentStep: WizardStep;
  needsGeocoding: boolean;
}) {
  const steps = getVisibleSteps(needsGeocoding);
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <nav className="flex items-center justify-center gap-2 mb-8">
      {steps.map((s, i) => {
        const isCompleted = currentIndex > i;
        const isCurrent = currentIndex === i;
        return (
          <div key={s.key} className="flex items-center">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium border-2 transition-colors",
                  isCompleted &&
                    "bg-primary border-primary text-primary-foreground",
                  isCurrent && "border-primary text-primary bg-primary/10",
                  !isCompleted &&
                    !isCurrent &&
                    "border-muted-foreground/30 text-muted-foreground/50",
                )}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  "text-sm font-medium hidden sm:block",
                  isCurrent && "text-foreground",
                  !isCurrent && "text-muted-foreground",
                )}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "w-8 sm:w-12 h-0.5 mx-2",
                  currentIndex > i ? "bg-primary" : "bg-muted-foreground/20",
                )}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ImportPage() {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-foreground mb-2">
        Import Document
      </h1>
      <p className="text-muted-foreground mb-6">
        Upload a CSV, XLSX, or Shapefile (ZIP) to import official locations.
      </p>

      <Stepper
        currentStep={state.step}
        needsGeocoding={state.needsGeocoding}
      />

      {state.step === "upload" && (
        <UploadStep state={state} dispatch={dispatch} />
      )}
      {state.step === "mapping" && (
        <MappingStep state={state} dispatch={dispatch} />
      )}
      {state.step === "address" && (
        <AddressStep state={state} dispatch={dispatch} />
      )}
      {state.step === "preview" && (
        <PreviewStep state={state} dispatch={dispatch} />
      )}
      {state.step === "results" && (
        <ResultsStep state={state} dispatch={dispatch} />
      )}
    </div>
  );
}

export type { WizardState, WizardAction };
export default ImportPage;
